// 5 ago 2026 (Augusto: "sería bueno que se determinen los egresos
// recurrentes, como pagos de servicios... quizás cuando sean 2, se puede
// hacer un pop up para preguntar si es recurrente dado que se repitió el
// mes pasado"): lógica de detección compartida entre TransactionsPage.js
// (alta manual de una transacción) y StatementImporter.js (revisión de un
// estado de cuenta importado), para no duplicarla en los dos lugares.
//
// La idea es simple: si una transacción nueva tiene el mismo proveedor,
// un monto parecido, y ocurrió ~1 mes después de otra transacción ya
// existente, es probable que sea un pago recurrente (luz, agua,
// suscripciones, etc.) -- se sugiere, nunca se marca solo, siempre pide
// confirmación (ver RecurringCandidatesDialog.js).

// Tolerancia de monto: 10% -- servicios como luz/agua varían un poco mes
// a mes sin dejar de ser "el mismo pago recurrente".
const TOLERANCIA_MONTO_PCT = 0.1;

// Ventana de días entre dos ocurrencias para considerarlas "mensuales":
// 25-40 días cubre meses de 28-31 días con margen para que el pago no
// caiga exactamente el mismo día calendario cada vez (fin de semana,
// feriado, etc.).
const DIAS_MIN = 25;
const DIAS_MAX = 40;

const montosSimilares = (a, b) => {
  if (!a || !b) return false;
  return Math.abs(a - b) / Math.max(a, b) <= TOLERANCIA_MONTO_PCT;
};

const diasEntre = (fechaA, fechaB) => {
  const ms = Math.abs(new Date(fechaA).getTime() - new Date(fechaB).getTime());
  return ms / (1000 * 60 * 60 * 24);
};

const normalizarProveedor = (proveedor) => (proveedor || '').trim().toLowerCase();

// true si ya existe una serie recurrente activa con este nombre -- evita
// volver a sugerir algo que el usuario ya marcó como recurrente antes
// (ej. el mes siguiente, al importar el mismo pago de Netflix otra vez).
export const yaEsRecurrente = (proveedor, recurringItems) => {
  const p = normalizarProveedor(proveedor);
  if (!p) return false;
  return (recurringItems || []).some(
    (r) => r.activo !== false && normalizarProveedor(r.nombre) === p
  );
};

/**
 * @param nuevas transacciones a revisar, forma normalizada:
 *   { proveedor, monto, tipo: 'ingreso'|'gasto', fecha (ISO o Date),
 *     categoria, accountId, _ref (lo que sea que el llamador necesite
 *     para identificar la fila luego, ej. el id de la fila en la tabla) }
 * @param historial transacciones ya existentes del usuario, misma forma
 *   normalizada (proveedor/monto/tipo/fecha) -- típicamente
 *   state.transactions.list ya mapeado.
 * @param recurringItems ingresos+egresos recurrentes activos (para no
 *   sugerir un proveedor que ya es recurrente).
 * @returns [{ nueva, anterior }] un candidato por cada coincidencia --
 *   nueva = la transacción que dispara la sugerencia (con su `_ref`
 *   original), anterior = la ocurrencia previa que la respalda.
 */
export const detectarCandidatosRecurrentes = (nuevas, historial, recurringItems = []) => {
  const candidatos = [];
  (nuevas || []).forEach((nueva) => {
    const proveedor = normalizarProveedor(nueva.proveedor);
    if (!proveedor || !nueva.monto || !nueva.fecha) return;
    if (yaEsRecurrente(nueva.proveedor, recurringItems)) return;
    const match = (historial || []).find((h) => {
      if (normalizarProveedor(h.proveedor) !== proveedor) return false;
      if (h.tipo !== nueva.tipo) return false;
      if (!montosSimilares(h.monto, nueva.monto)) return false;
      const dias = diasEntre(h.fecha, nueva.fecha);
      return dias >= DIAS_MIN && dias <= DIAS_MAX;
    });
    if (match) candidatos.push({ nueva, anterior: match });
  });
  return candidatos;
};

// Arma el payload para POST /recurring/incomes o /recurring/expenses a
// partir de una transacción real (importada o creada a mano), anclando
// la recurrencia al día del mes de esa transacción -- misma lógica de
// "próxima ocurrencia" que ya usa TransactionsPage.js para el switch
// manual "¿Es recurrente?" (ver buildRecurringPayload ahí), pero
// calculando el día a partir de la fecha de la transacción en vez de
// siempre "hoy", porque acá la transacción disparadora puede ser de un
// estado de cuenta ya importado (su fecha no es necesariamente hoy).
//
// 30 ago 2026 (pedido de Augusto: marcar transacciones ya existentes
// como recurrentes, una por una desde "Editar Transacción" o en masa
// desde la lista): se agrega el parámetro `frecuencia` (antes hardcodeada
// a "mensual") -- "diario" y "quincenal" (días 15 y 30 fijos) no
// necesitan anclarse a un día del mes, así que para esos casos
// fecha_inicio es simplemente hoy, igual que ya hace buildRecurringPayload
// en TransactionsPage.js para una transacción nueva.
export const buildRecurringPayloadDesdeTransaccion = (tx, frecuencia = 'mensual') => {
  let fecha_inicio;
  if (frecuencia === 'mensual') {
    const fechaTx = tx.fecha ? new Date(tx.fecha) : new Date();
    const diaElegido = fechaTx.getDate();
    const hoy = new Date();
    let mes = hoy.getMonth();
    let anio = hoy.getFullYear();
    if (diaElegido < hoy.getDate()) {
      mes += 1;
      if (mes > 11) {
        mes = 0;
        anio += 1;
      }
    }
    const ultimoDiaMes = new Date(anio, mes + 1, 0).getDate();
    const diaFinal = Math.min(diaElegido, ultimoDiaMes);
    fecha_inicio = new Date(anio, mes, diaFinal).toISOString().slice(0, 10);
  } else {
    fecha_inicio = new Date().toISOString().slice(0, 10);
  }
  return {
    nombre: (tx.proveedor || '').trim() || (tx.tipo === 'ingreso' ? 'Ingreso recurrente' : 'Egreso recurrente'),
    monto: tx.monto,
    frecuencia,
    categoria: tx.categoria || null,
    account_id: tx.accountId ?? null,
    // 30 ago 2026 (Augusto: "se necesita colocar tanto en Soles como su
    // equivalente en Dólares" en Recurrentes): se conserva la moneda de la
    // transacción origen -- si venía de un estado de cuenta en dólares
    // (moneda_override), la serie recurrente que se crea a partir de ella
    // también queda marcada en dólares, en vez de perderse esa info.
    moneda: tx.moneda || null,
    fecha_inicio,
    fecha_fin: null,
  };
};
